<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useDateFormat } from '@vueuse/core';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useQRCode } from '@vueuse/integrations/useQRCode';
import Gallery from './UserPage/components/Gallery.vue';
import { usePdfGenerator } from '../composables/usePdfGenerator';

const { t } = useI18n({ useScope: 'global' });
const apiUrl = import.meta.env.VITE_API_URL;
const { triggerPdfGeneration } = usePdfGenerator();
const route = useRoute();

const result = ref({});
const attempt_id = ref(route.params.attemptId);
const qrcode = useQRCode(`${result?.video}`);

const getResultData = async () => {
    try {
        const response = await axios.get(`${apiUrl}/v1/file_generate`, {
            params: {
                attempt_id: attempt_id.value,
            },
        });
        result.value = response.data.data;
    } catch (error) {
        console.error(error);
    }
};

const groupImages = (item) => {
    const groups = {};
    const images = item?.before_exam || [];

    images.forEach((img) => {
        const date = useDateFormat(img.created_at, 'DD.MM.YYYY').value;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(img);
    });

    return groups;
};

const handlePdfGeneration = async () => {
    triggerPdfGeneration(result.value);
};

onMounted(async () => {
    getResultData();
});
</script>

<template>
    <div class="result-header">
        <p>{{ result.full_name }}.pdf</p>
        <Button severity="info" :label="t('profile.pdfDownload')" icon="pi pi-file-pdf" @click="handlePdfGeneration" />
    </div>
    <div class="result-container">
        <h1 class="result-title">{{ t('profile.title') }}</h1>
        <div class="result-wrapper">
            <img :src="result.photo" class="user-img" />
            <div class="result-info">
                <p>
                    <b>{{ t('profile.fio') }}:</b> {{ result.full_name }}
                </p>
                <p>
                    <b>{{ t('profile.score') }}:</b> {{ result.score }}
                </p>
                <p>
                    <b>{{ t('profile.passScore') }}:</b> {{ result.base_score }}
                </p>
                <p>
                    <b>{{ t('global.duration') }}:</b> {{ result.duration ? result.duration : '-' }}
                </p>
                <p>
                    <b>{{ t('global.start') }}:</b> {{ result.started_at ? useDateFormat(result.started_at, 'HH:mm').value : '-' }}
                </p>
                <p>
                    <b>{{ t('profile.violations') }}:</b> <span class="item-value">{{ result.cheating_count ? result.cheating_count : '-' }}</span>
                </p>
                <p>
                    <b>{{ t('global.status') }}:</b> <span :class="result.status == 'PASSED' ? 'text-green-500' : 'text-red-500'">{{ t(`profile.${result?.status}`) }}</span>
                </p>
                <p>
                    <b>{{ t('global.sessionName') }}:</b> {{ result.name }}
                </p>
                <p>
                    <b>{{ t('profile.examID') }}:</b> {{ result.exam_id }}
                </p>
                <div class="qr-code" v-if="result.video && !result.is_video_deleted">
                    <img :src="qrcode" alt="QR Code" />
                    <p>{{ t('profile.videoScan', { date: useDateFormat(result.video_valid_date, 'DD.MM.YYYY').value }) }}</p>
                </div>
                <p v-else-if="!result.video" class="error-text">{{ t('profile.videoError') }}</p>
                <p v-else class="error-text">{{ t('profile.videoDeleted') }}</p>
            </div>
        </div>

        <DataTable :value="result.cheatings">
            <template #empty> {{ t('global.noData') }}</template>
            <Column field="code" :header="t('profile.code')">
                <template #body="{ data }">
                    {{ data.code.toUpperCase() }}
                </template>
            </Column>
            <Column field="code" :header="t('profile.desc')">
                <template #body="{ data }">
                    {{ t(`violations.${data.code}.long`) }}
                </template>
            </Column>
            <Column field="score" :header="t('profile.penalty')">
                <template #body="{ data }">
                    <span>{{ data.score }}</span>
                </template>
            </Column>
            <Column field="created_at" :header="t('profile.time')">
                <template #body="{ data }">
                    <span>{{ useDateFormat(data.created_at, 'HH:mm:ss').value }}</span>
                </template>
            </Column>
        </DataTable>

        <Htag v-if="result.before_exam?.length" tag="h1" class="gallery-title"> {{ t('profile.beforeExam') }}: </Htag>

        <div v-for="(images, date) in groupImages(result)" :key="date" class="date-group">
            <Htag tag="h2">{{ date }}</Htag>
            <div class="gallery-wrapper">
                <Gallery v-for="img in images" :key="img.created_at" id="1" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" />
            </div>
        </div>

        <Htag v-if="result.during_exam?.length" tag="h1" class="gallery-title"> {{ t('profile.duringExam') }}: </Htag>

        <div class="gallery-wrapper">
            <Gallery v-for="img in result.during_exam" :key="img.created_at" id="2" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" />
        </div>
    </div>
</template>

<style scoped>
.result-header {
    @apply flex items-center justify-between p-4 shadow-md bg-white sticky top-0 z-50;
}
.result-container {
    @apply bg-white w-[800px] m-auto h-full p-12;
}
.result-wrapper {
    @apply flex gap-8 items-start;
}
.result-title {
    @apply text-2xl font-bold text-center mb-8;
}
.user-img {
    @apply w-[250px];
}
.gallery-wrapper {
    @apply flex flex-wrap gap-4;
}

:deep.gallery-wrapper .gallery {
    @apply w-[100px] h-[100px];
}

.gallery-title {
    @apply my-4;
}

.date-group {
    @apply my-4;
}

.qr-code {
    @apply flex items-center gap-4;
}

.qr-code img {
    @apply w-[120px] h-[120px];
}

.error-text {
    @apply text-red-500 mt-[5px];
}
</style>
